import { Button } from '@progress/kendo-react-buttons';
import { Card, GridLayout, GridLayoutItem } from '@progress/kendo-react-layout';
import EvEquipmentIcon from 'assets/images/ic_equipment_ev.svg';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EditNickname } from '../EditNickname';

import CriticalBateryIcon from 'assets/images/ic_tele_battery_critical.svg';
import NormalBateryIcon from 'assets/images/ic_tele_battery_normal.svg';
import WarningBateryIcon from 'assets/images/ic_tele_battery_warning.svg';
import { ClockIcon } from 'app/components/icons';

import AssetTriangleIcon from 'assets/images/ic_tele_error.svg';
import CriticalTemperatureIcon from 'assets/images/ic_tele_temperature_critical.svg';
import NormalTemperatureIcon from 'assets/images/ic_tele_temperature_normal.svg';
import WarningTemperatureIcon from 'assets/images/ic_tele_temperature_warning.svg';

import GeofenceIcon from 'assets/images/ic_res_geofences.svg';

import IntelIcon from 'assets/images/ic_equipment_intellattach.svg';
import EquipmentLinkIcon from 'assets/images/ic_equipment_link.svg';
import FaultCodeIcon from 'assets/images/ic_res_fault_code-2.svg';
import RessIntelAttachIcon from 'assets/images/ic_res_intell_attach.svg';
import MaintenanceScheduleIcon from 'assets/images/ic_res_maint_schedule.svg';
import MaintenanceGuides from 'assets/images/ic_res_maintenance_guides.svg';
import TrackMaintenanceIcon from 'assets/images/ic_res_maintenance_track.svg';
import ManualsIcon from 'assets/images/ic_res_manuals.svg';
import VideoResourcesIcon from 'assets/images/ic_res_video.svg';
import WarrantyInformationIcon from 'assets/images/ic_res_warranty_info.svg';

import { Dialog } from '@progress/kendo-react-dialogs';
import { ErrorModal } from 'app/components/ErrorModal';
import { Success } from 'app/components/Notifications';
import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import { MaintenanceHistoryContext } from 'app/components/ReactContexts/maintenanceHistoryContext';
import * as utils from 'app/pages/TrackMaintenencePage/maintenanceUtils';
import ChevronRightIcon from 'assets/images/chevron_right.svg';
import React, { useMemo } from 'react';
import { MaintenanceHistory } from 'types/MaintenanceHistory';
import { LocationUnavailableGeofenceDialog } from '../Geofence/LocationUnavailableGeofenceDialog';
import { CompatibleAttachments } from './CompatibleAttachmentsContent';
import { DeleteEqupmentDialog } from './DeleteEquiprmentDialog';
import { DeleteTelematicsEquipmentErrorDialog } from './DeleteTelematicsEquipmentErrorDialog';

import { GoogleMap, MarkerF, useLoadScript } from '@react-google-maps/api';
import { librariesList } from 'app/common/librariesList';
import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import {
  ic_nav_back as IconBack,
  ic_maintenance_edit as MaintenanceEditIcon,
  ic_btn_delete as DeleteIcon,
} from 'app/widgets/SVGs';
import _ from 'lodash';

import { getDefaultThumbnail } from 'app/pages/Equipment/utils';
import { EditEngineHours, getEngineHours } from '../EditEngineHours';

import MapUnavailablePinIcon from 'assets/images/ic_tele_geofence_unavailable_content.svg';
import DealerIcon from 'assets/images/map_pin.svg';

import { isCanada } from 'app/common/websiteCountry';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import { getLocale } from 'locales/utils';
import { tagEvent } from 'utils/ga';
import {
  convertNumberToDistance,
  formatNumberWithDecimals,
  formatSeconds,
} from 'utils/general';
import { CompatibleMachines } from './CompatibleMachinesContent';
import { SubscriptionBlock, EquipmentAccessSharingButton } from './components';
import './EquipmentContent.less';
import useFlag from 'app/hooks/useFlag';
import useHasAccessSharingPermissions from 'app/hooks/useHasAccessSharingPermissions';
import { FeatureName } from 'types/entities/MobileSettings';
import { useManageYourPlanDialog, useNotificationsContext } from 'context';
import {
  GeofenceIcon as GeofenceIconNew,
  NoNotificationIcon,
  DiamondIcon,
} from 'app/components/icons';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from 'app/components/List';
import { ActionsListItem, EllipsisMenu } from 'app/components/menu';
import {
  InMotionStatusIcon,
  RestartInhibitStatusIcon,
} from 'app/components/equipment';
import useGetEquipmentData from 'app/hooks/useGetEquipmentData';
import CancelledRequestError from 'errors/CancelledRequestError';
import { WordmarkLoader } from 'app/components/loaders';
import { InhibitRestartButton } from 'app/pages/Equipment/EquipmentContent/components/InhibitRestartButton';
import useSubscriptionInfo from 'app/hooks/subscriptions/useSubscriptionInfo';
import { useTelematicsBanner } from 'app/hooks/useTelematicsBanner';
import { WarningNotification } from 'app/components/Notifications/WarningNotification';
import { PlanChangedSuccessfullyDialog } from 'app/pages/Equipment/components';
import { BannerType } from 'utils/banner';
import { axiosInstance } from 'services/api';
import { api } from 'services/api';

interface Props {
  isSubPlanChangedSuccessfullyDialogVisibility?: boolean;
}

export function EquipmentContent({
  isSubPlanChangedSuccessfullyDialogVisibility,
}: Props) {
  const { t, i18n } = useTranslation();
  const isSubscriptionsFeatureEnabled = useFlag(FeatureName.SUBSCRIPTIONS);
  const isAccessSharingFeatureEnabled = useFlag(FeatureName.ACCESS_SHARING);

  const { data: { measurementUnit } = {} } = api.useGetUserSettingsQuery();

  const { equipmentListData: data, setEquipmentListData: setData } =
    React.useContext(EquipmentListContext);
  const { maintenanceHistoryData, setMaintenanceHistoryData } =
    React.useContext(MaintenanceHistoryContext);
  const { addNotification, removeNotification } = useNotificationsContext();
  const { addErrorModal } = React.useContext(GlobalErrorContext);

  const [nicknameIsExpanded, setNicknameIsExpanded] = React.useState(false);
  const [engineHoursIsExpanded, setEngineHoursIsExpanded] =
    React.useState(false);

  const [showDeleteEquipmentDialog, setShowDeleteEquipmentDialog] =
    React.useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = React.useState(false);
  const [showErrorModal, setShowErrorModal] = React.useState<boolean>(false);
  const [
    showDeleteTelematicsEquipmentErrorModal,
    setShowDeleteTelematicsEquipmentErrorModal,
  ] = React.useState<boolean>(false);
  const [showLocationUnavailableDialog, setShowLocationUnavailableDialog] =
    React.useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const { openDialog: openManageYourPlanDialog } = useManageYourPlanDialog();

  const { selectedEquipment: equipmentData } = useGetEquipmentData(
    data.selectedEquipment?.id,
  );

  const { role } = equipmentData || {};
  const hasAccess = useHasAccessSharingPermissions(role);
  const subscriptionInfo = useSubscriptionInfo(equipmentData?.subscriptions);
  const ellipsisMenuVisibility = !role || hasAccess('editEquipment');

  const smartAttachmentInfo = equipmentData?.telematics?.smartAttachment?.serial
    ? data.equipmentList?.find(
        s => s.serial === equipmentData?.telematics?.smartAttachment?.serial,
      )
    : null;
  const smartAttachmentImageUrl =
    smartAttachmentInfo?.modelIconUrl ||
    getDefaultThumbnail(
      smartAttachmentInfo?.type,
      smartAttachmentInfo?.subcategory,
    );

  const isEV = !!equipmentData?.telematics?.electric;
  const isTelematics = !!equipmentData?.telematics;
  const isRestartInhibitUnavailable =
    !equipmentData?.telematics?.restartInhibitStatus;

  const { removeSpecificClass } = React.useContext(LayoutContext);
  const telematicsBannerIdRef = React.useRef<string | null>(null);

  const handleClick = () => {
    removeSpecificClass('equipment-details-visible');

    if (telematicsBannerIdRef.current) {
      removeNotification(telematicsBannerIdRef.current);
      telematicsBannerIdRef.current = null;
    }
  };
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.GOOGLE_API_KEY as string,
    libraries: librariesList,
  });

  const [maintenanceLoading, setMaitenanceLoading] = React.useState(false);

  React.useEffect(() => {
    if (
      isLoaded &&
      data.selectedEquipment?.telematics?.location?.latitude &&
      data.selectedEquipment?.telematics?.location?.longitude
    ) {
      new google.maps.Geocoder()
        .geocode({
          location: {
            lat: data.selectedEquipment?.telematics?.location.latitude,
            lng: data.selectedEquipment?.telematics?.location.longitude,
          },
        })
        .then(result => {
          setData(oldData => ({
            ...oldData,
            geoAddress: result.results[0].formatted_address,
          }));
        })
        .catch(e => {
          setData(oldData => ({
            ...oldData,
            geoAddress: null,
          }));
        });
    }

    if (
      isLoaded &&
      (!data.selectedEquipment?.telematics?.location?.latitude ||
        !data.selectedEquipment?.telematics?.location?.longitude)
    ) {
      setData(oldData => ({
        ...oldData,
        geoAddress: null,
      }));
    }
  }, [isLoaded, data.selectedEquipment?.id]);

  React.useEffect(() => {
    (async () => {
      if (_.isNil(params.id)) {
        return;
      }

      if (equipmentData?.type !== 'machine') {
        return;
      }

      const equipment = utils.getSelectedEquipment(data, params.id);

      try {
        setMaitenanceLoading(true);
        const maintenanceHistoryResponse = await axiosInstance.get(
          `/api/user/equipment/${params.id}/maintenanceHistory`,
        );

        const maintenanceScheduleResponse = await axiosInstance.get(
          `/api/maintenanceSchedule/${equipment?.model}`,
          {
            headers: {
              version: '2022_R03',
            },
          },
        );
        setMaitenanceLoading(false);

        setMaintenanceHistoryData({
          ...maintenanceHistoryData,
          selectedEquipmentMaintenanceHistory:
            utils.getSortedMaintenanceHistory(maintenanceHistoryResponse.data),
          rawMaintenanceIntervals: maintenanceScheduleResponse.data,
          maintenanceIntervals: utils.getProcessedMaintenanceSchedule(
            maintenanceScheduleResponse.data,
            true,
          ),
        });
      } catch (err: any) {
        if (err instanceof CancelledRequestError) {
          return;
        }
        addErrorModal();
      }
    })();
  }, [equipmentData?.id, params.id]);

  const testRef = React.useRef<any>();

  const [errorErrorTitle] = React.useState<string>('');
  const [errorMessage, setErrorMessage] = React.useState<string>();

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (event.target.className === 'k-overlay') {
        event.stopPropagation();
        setShowDeleteEquipmentDialog(false);
        setNicknameIsExpanded(false);
        setEngineHoursIsExpanded(false);
        setShowDeleteTelematicsEquipmentErrorModal(false);
        setShowLocationUnavailableDialog(false);
        setShowErrorModal(false);
        localStorage.setItem('justLoggedOn', 'false');
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  const {
    showExpiresSoon,
    dismissExpiresSoon,
    dismissUnlockFeatures,
    showUnlockFeatures,
  } = useTelematicsBanner({
    equipmentId: equipmentData?.id ?? '',
    isSubscriptionsFeatureEnabled,
    expiresSoon: subscriptionInfo.endsWithinDays,
    isBasicTelematics:
      !subscriptionInfo.hasPremium && !!equipmentData?.telematics,
    ttlDays: 30,
  });

  React.useLayoutEffect(() => {
    if (!telematicsBannerIdRef.current) {
      switch (true) {
        case showExpiresSoon:
          telematicsBannerIdRef.current = addNotification(
            <WarningNotification
              key={equipmentData?.id}
              title={{
                i18nKey: subscriptionInfo.isInitial
                  ? 'subscriptions.equipment_card.initial_ends_on_highlighted'
                  : 'subscriptions.equipment_card.premium_ends_on_highlighted',
                values: {
                  date: subscriptionInfo.endsOn,
                },
              }}
              subtitle={{
                i18nKey: 'subscriptions.notifications.expires_soon.subtitle',
              }}
              link={{
                i18nKey: 'subscriptions.notifications.expires_soon.link',
                onClick: () =>
                  equipmentData &&
                  openManageYourPlanDialog({
                    equipment: equipmentData,
                  }),
              }}
            />,
            'content-centered',
            null,
            dismissExpiresSoon,
            `${BannerType.SUBSCRIPTION_EXPIRES_SOON}-${equipmentData?.id}`,
          );
          break;
        case showUnlockFeatures:
          telematicsBannerIdRef.current = addNotification(
            <WarningNotification
              key={equipmentData?.id}
              title={{
                i18nKey: 'subscriptions.notifications.more_features.title',
              }}
              subtitle={{
                i18nKey: 'subscriptions.notifications.more_features.subtitle',
              }}
            />,
            'content-centered',
            null,
            dismissUnlockFeatures,
            `${BannerType.SUBSCRIPTION_UNLOCK_FEATURES}-${equipmentData?.id}`,
          );
          break;
      }
    }

    return () => {
      if (telematicsBannerIdRef.current) {
        removeNotification(telematicsBannerIdRef.current);
        telematicsBannerIdRef.current = null;
      }
    };
  }, [showUnlockFeatures, equipmentData?.id]);

  const actions = useMemo<ActionsListItem[]>(() => {
    return [
      {
        label: (
          <div className="equipment-content-dropdown__item">
            <MaintenanceEditIcon color="#909090" width={24} height={24} />
            {t('edit_nickname')}
          </div>
        ),
        action: () => setNicknameIsExpanded(true),
        exclude: !hasAccess('editEquipment'),
      },
      {
        label: (
          <div className="equipment-content-dropdown__item">
            <MaintenanceEditIcon color="#909090" width={24} height={24} />
            {t('maintenance_edit_engine_hours')}
          </div>
        ),
        action: () => setEngineHoursIsExpanded(true),
        exclude: isTelematics || !hasAccess('editEquipment'),
      },
      {
        label: (
          <div className="equipment-content-dropdown__item">
            <DeleteIcon color="#909090" width={24} height={24} />
            {t('equipment_delete_menu_item_title')}
          </div>
        ),
        action: () => setShowDeleteEquipmentDialog(true),
        exclude: !hasAccess('deleteEquipment'),
      },
    ];
  }, [
    t,
    isTelematics,
    setNicknameIsExpanded,
    setEngineHoursIsExpanded,
    setShowDeleteEquipmentDialog,
    hasAccess,
  ]);

  if (!equipmentData) {
    return <WordmarkLoader title={t('loading_equipment...')} />;
  }

  const aproximateToTwo = num => {
    return Math.round(num * 100) / 100;
  };

  const temperatureUnitConverted = (value?: number) => {
    if (measurementUnit === 'METRIC') {
      return value;
    } else {
      if (!value) {
        return value;
      }
      return (value * 9) / 5 + 32;
    }
  };

  const lastHistoryInterval = utils.getLastHistoryInterval(
    maintenanceHistoryData.selectedEquipmentMaintenanceHistory,
  ) as MaintenanceHistory;

  const locale = getLocale();
  let lastUpdate = '';
  if (lastHistoryInterval) {
    lastUpdate =
      isCanada || locale === 'fr-CA' || locale === 'en-CA'
        ? utils.getCanadaFormateDate(
            lastHistoryInterval.updatedDate || '',
            'date',
            locale,
          )
        : utils.formatDate(lastHistoryInterval.updatedDate, 'mmm dd yyyy');
  }

  const nextHours =
    lastHistoryInterval &&
    utils.getNextScheduleInterval(
      maintenanceHistoryData.maintenanceIntervals,
      maintenanceHistoryData.selectedEquipmentMaintenanceHistory,
      lastHistoryInterval,
      Number(getEngineHours(data)),
    );
  const hasFuel = !!(
    equipmentData.telematics?.fuelRemainingPercent ||
    equipmentData.telematics?.fuelRemainingPercent === 0
  );
  const hasDef =
    equipmentData.telematics?.defRemainingPercent ||
    equipmentData.telematics?.defRemainingPercent === 0;
  const hasFuelOrDef = hasFuel || hasDef;
  const mapContainerStyle = {
    width: '100%',
    height: '130px',
    filter: data.geoAddress == null ? 'blur(10px)' : 'none',
  };

  const height = 68;
  const marginBottom = 2;
  const batteryPercent =
    equipmentData.telematics?.electric?.battery.remainingCharge ?? 0;
  const fillColor =
    batteryPercent > 25.99
      ? '#1AAD7F'
      : batteryPercent > 10
      ? '#E88F26'
      : '#FF0700';

  const filledHeight = (batteryPercent / 100) * height;

  const chargerSvg = (
    <g id="icon" transform="translate(11.779 24.918)">
      <g
        id="Icon-2"
        data-name="Icon"
        transform="translate(-0 4)"
        fill="#131313"
        strokeLinejoin="round"
      >
        <path
          d="M 6.230673789978027 18.95959663391113 L 5.756923675537109 18.84970664978027 L 5.715899467468262 12.46369647979736 L 1.172853708267212 12.46369647979736 L 0.7642937302589417 12.46369647979736 L 0.9502537250518799 12.0999059677124 L 6.816513538360596 0.6234164237976074 L 7.289103507995605 0.7399163842201233 L 7.219653606414795 7.13844633102417 L 7.219659805297852 7.515636444091797 L 11.52929401397705 7.515636444091797 L 11.93318367004395 7.515636444091797 L 11.75305366516113 7.87713623046875 L 6.230673789978027 18.95959663391113 Z"
          fill="#000"
        />
        <path
          d="M 6.006913661956787 18.84809684753418 L 11.52929401397705 7.765636444091797 L 6.969663619995117 7.765636444091797 L 6.969663619995117 7.135736465454102 L 7.039113521575928 0.7372063994407654 L 1.172853708267212 12.21369647979736 L 5.964293956756592 12.21369647979736 L 6.006913661956787 18.84809684753418 M 6.007037162780762 19.34814453125 C 5.969569206237793 19.34814453125 5.931674957275391 19.34392166137695 5.893933773040771 19.33516693115234 C 5.668503761291504 19.28287696838379 5.508413791656494 19.08271598815918 5.506923675537109 18.8513069152832 L 5.467494964599609 12.71369647979736 L 1.172853708267212 12.71369647979736 C 0.9987737536430359 12.71369647979736 0.8372237682342529 12.62315654754639 0.7463637590408325 12.47466659545898 C 0.6555037498474121 12.32618618011475 0.6484137177467346 12.14112663269043 0.7276437282562256 11.98612594604492 L 6.593903541564941 0.509636402130127 C 6.699533939361572 0.3029963672161102 6.933462619781494 0.1961964666843414 7.158783912658691 0.2517364025115967 C 7.384113788604736 0.307286411523819 7.541603565216064 0.5105764269828796 7.539083957672119 0.7426363825798035 L 7.469663619995117 7.265636444091797 L 11.52929401397705 7.265636444091797 C 11.70246410369873 7.265636444091797 11.86330413818359 7.355236530303955 11.95444393157959 7.502476215362549 C 12.04558372497559 7.649726390838623 12.05404376983643 7.833646297454834 11.97681331634521 7.988636493682861 L 6.454433917999268 19.07109642028809 C 6.368503093719482 19.24353981018066 6.193366050720215 19.34814453125 6.007037162780762 19.34814453125 Z"
          stroke="none"
          fill="#fff"
        />
      </g>
    </g>
  );

  const pluggedSvg = (
    <g
      id="Icon"
      transform="translate(13.5 28.5)"
      fill="#1a1a1a"
      strokeLinecap="round"
    >
      <path
        d="M 5.000400066375732 15.25030040740967 C 4.311200141906738 15.25030040740967 3.750499963760376 14.68918991088867 3.750499963760376 13.9995002746582 L 3.750499963760376 12.09997653961182 C 1.420394062995911 11.53111267089844 -0.2500000596046448 9.418641090393066 -0.2500000596046448 7.000200271606445 L -0.2500000596046448 3.999600172042847 L -0.2500000596046448 3.749600172042847 L -6.790160966829717e-08 3.749600172042847 L 1.25029993057251 3.749600172042847 L 1.25029993057251 0.999900221824646 C 1.25029993057251 0.3107002079486847 1.810999989509583 -0.2499997913837433 2.500200033187866 -0.2499997913837433 C 3.18939995765686 -0.2499997913837433 3.750099897384644 0.3107002079486847 3.750099897384644 0.999900221824646 L 3.750099897384644 3.749600172042847 L 6.249799728393555 3.749600172042847 L 6.249799728393555 0.999900221824646 C 6.249799728393555 0.3107002079486847 6.810500144958496 -0.2499997913837433 7.49970006942749 -0.2499997913837433 C 8.189390182495117 -0.2499997913837433 8.750499725341797 0.3107002079486847 8.750499725341797 0.999900221824646 L 8.750499725341797 3.749600172042847 L 9.999899864196777 3.749600172042847 L 10.24989986419678 3.749600172042847 L 10.24989986419678 3.999600172042847 L 10.24989986419678 7.000200271606445 C 10.24989986419678 9.418350219726562 8.579889297485352 11.53078079223633 6.25029993057251 12.09988212585449 L 6.25029993057251 13.9995002746582 C 6.25029993057251 14.68918991088867 5.689599990844727 15.25030040740967 5.000400066375732 15.25030040740967 Z"
        stroke="none"
      />
      <path
        d="M 5.000400066375732 15.00030040740967 C 5.552099704742432 15.00030040740967 6.00029993057251 14.55210018157959 6.00029993057251 13.9995002746582 L 6.00029993057251 11.89965057373047 C 8.282320022583008 11.43642044067383 9.999899864196777 9.418950080871582 9.999899864196777 7.000200271606445 L 9.999899864196777 3.999600172042847 L 8.500499725341797 3.999600172042847 L 8.500499725341797 0.999900221824646 C 8.500499725341797 0.4473001956939697 8.052299499511719 2.037048290048915e-07 7.49970006942749 2.037048290048915e-07 C 6.947999954223633 2.037048290048915e-07 6.499799728393555 0.4473001956939697 6.499799728393555 0.999900221824646 L 6.499799728393555 3.999600172042847 L 3.500099897384644 3.999600172042847 L 3.500099897384644 0.999900221824646 C 3.500099897384644 0.4473001956939697 3.052799940109253 2.037048290048915e-07 2.500200033187866 2.037048290048915e-07 C 1.9475998878479 2.037048290048915e-07 1.50029993057251 0.4473001956939697 1.50029993057251 0.999900221824646 L 1.50029993057251 3.999600172042847 L -6.790160966829717e-08 3.999600172042847 L -6.790160966829717e-08 7.000200271606445 C -6.790160966829717e-08 9.419110298156738 1.717639923095703 11.43665981292725 4.000499725341797 11.8997106552124 L 4.000499725341797 13.9995002746582 C 4.000499725341797 14.55210018157959 4.447800159454346 15.00030040740967 5.000400066375732 15.00030040740967 M 5.000400066375732 15.50030040740967 C 4.173349857330322 15.50030040740967 3.500499963760376 14.82704067230225 3.500499963760376 13.9995002746582 L 3.500499963760376 12.2926139831543 C 1.159367561340332 11.63050746917725 -0.5000000596046448 9.467310905456543 -0.5000000596046448 7.000200271606445 L -0.5000000596046448 3.499600172042847 L 1.00029993057251 3.499600172042847 L 1.00029993057251 0.999900221824646 C 1.00029993057251 0.1728502064943314 1.673149943351746 -0.4999997913837433 2.500200033187866 -0.4999997913837433 C 3.327250003814697 -0.4999997913837433 4.000100135803223 0.1728502064943314 4.000100135803223 0.999900221824646 L 4.000100135803223 3.499600172042847 L 5.999799728393555 3.499600172042847 L 5.999799728393555 0.999900221824646 C 5.999799728393555 0.1728502064943314 6.67264986038208 -0.4999997913837433 7.49970006942749 -0.4999997913837433 C 8.327239990234375 -0.4999997913837433 9.000499725341797 0.1728502064943314 9.000499725341797 0.999900221824646 L 9.000499725341797 3.499600172042847 L 10.49989986419678 3.499600172042847 L 10.49989986419678 7.000200271606445 C 10.49989986419678 9.467007637023926 8.840909004211426 11.63015842437744 6.50029993057251 12.29249954223633 L 6.50029993057251 13.9995002746582 C 6.50029993057251 14.82704067230225 5.827449798583984 15.50030040740967 5.000400066375732 15.50030040740967 Z"
        stroke="none"
        fill="#fff"
      />
    </g>
  );

  const warningSvg = (
    <>
      <g id="Icon" transform="translate(16 32)">
        <g id="Path" fill="#ff0700" stroke="#fff" strokeWidth="0.5">
          <rect width="4" height="11" stroke="none" />
          <rect x="-0.25" y="-0.25" width="4.5" height="11.5" fill="none" />
        </g>
        <g
          id="Path-2"
          data-name="Path"
          transform="translate(0 14)"
          fill="#ff0700"
          stroke="#fff"
          strokeWidth="0.5"
        >
          <rect width="4" height="4" stroke="none" />
          <rect x="-0.25" y="-0.25" width="4.5" height="4.5" fill="none" />
        </g>
      </g>
    </>
  );

  const batteryStatusSvg =
    batteryPercent < 10
      ? warningSvg
      : equipmentData.telematics?.electric?.battery.charging
      ? chargerSvg
      : equipmentData.telematics?.electric?.battery.chargerPresent
      ? pluggedSvg
      : null;

  const batteryStatusState = equipmentData.telematics?.electric?.battery
    .fullCharge
    ? t('fully_charged')
    : equipmentData.telematics?.electric?.battery.charging
    ? ` ${t('until_fully_charged', {
        time: formatSeconds(
          equipmentData.telematics?.electric?.battery.timeUntilFullCharge,
        ),
      })} `
    : equipmentData.telematics?.electric?.battery.chargerPresent
    ? t('not_charging')
    : null;
  const batteryHasState = !!batteryStatusState;

  const onDeleteEquipment = () => {
    setShowLoadingOverlay(true);
    axiosInstance
      .delete(`/api/user/equipment/${equipmentData.id}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(() => {
        const equipmentList = data.equipmentList?.filter(e => {
          return e.id !== data.selectedEquipment?.id;
        });
        const isNotEmpty = equipmentList?.length && equipmentList.length > 0;
        setData(oldData => ({
          ...oldData,
          selectedEquipment: isNotEmpty ? equipmentList[0] : undefined,
          selectedIndex: isNotEmpty ? 0 : undefined,
          equipmentList,
        }));
        if (isNotEmpty) navigate(`/equipment/${equipmentList[0].id}`);
        addNotification(
          <Success text={t('equipment_delete_notification_success')} />,
        );
        tagEvent('delete_equipment');
        setShowLoadingOverlay(false);
        setShowDeleteEquipmentDialog(false);
      })
      .catch(ex => {
        if (ex instanceof CancelledRequestError) {
          return;
        }

        if (
          ex.response.data === 'Cannot delete automatically added equipment'
        ) {
          setShowDeleteTelematicsEquipmentErrorModal(true);
        } else {
          setErrorMessage(ex.response.data);
          setShowErrorModal(true);
        }
        setShowLoadingOverlay(false);
        setShowDeleteEquipmentDialog(false);
      });
  };
  const getDate = () => {
    const locationTime = equipmentData.telematics?.locationTime || '';

    return isCanada || locale === 'fr-CA' || locale === 'en-CA'
      ? utils.getCanadaFormateDate(locationTime, 'date_time', locale)
      : utils.getUSFormateDate(locationTime, locale);
  };

  const getHours = date => {
    var currentEngineHours = 0;
    if (data.selectedEquipment?.hasTelematics)
      currentEngineHours = parseFloat(
        formatNumberWithDecimals(getEngineHours(date), 1),
      );
    else
      currentEngineHours = parseFloat(
        formatNumberWithDecimals(getEngineHours(date), 2),
      );
    return i18n.language === 'fr' ||
      isCanada ||
      locale === 'fr-CA' ||
      locale === 'en-CA'
      ? formatFrHours(currentEngineHours)
      : currentEngineHours;
  };

  const formatFrHours = hours => {
    return hours.toString().replace('.', t('hours_separator'));
  };

  const getMaintenanceHours = hours => {
    return isCanada ||
      locale === 'fr-CA' ||
      locale === 'en-CA' ||
      i18n.language === 'fr'
      ? formatFrHours(formatNumberWithDecimals(hours, 2))
      : formatNumberWithDecimals(hours, 2);
  };

  return (
    <>
      {isSubscriptionsFeatureEnabled &&
        isSubPlanChangedSuccessfullyDialogVisibility && (
          <PlanChangedSuccessfullyDialog
            onBack={() => navigate(`/equipment/${equipmentData.id}`)}
          />
        )}

      {showErrorModal && (
        <Dialog className="modal-center modal-error modal-remove-item modal-fulltext">
          <ErrorModal
            title={errorErrorTitle || t('unexpected_error_title')}
            text={errorMessage || t('unexpected_error_message')}
            onClose={() => {
              setShowErrorModal(false);
              setErrorMessage('');
            }}
          />
        </Dialog>
      )}
      {showDeleteTelematicsEquipmentErrorModal && (
        <Dialog className="modal-center modal-remove-item modal-telematics-error modal-fulltext">
          <DeleteTelematicsEquipmentErrorDialog
            onClose={() => {
              setShowDeleteTelematicsEquipmentErrorModal(false);
              setErrorMessage('');
            }}
          />
        </Dialog>
      )}
      {showDeleteEquipmentDialog && (
        <Dialog
          className="modal-center modal-remove-item modal-fulltext"
          ref={testRef}
        >
          <DeleteEqupmentDialog
            isLoading={showLoadingOverlay}
            modelIconUrl={equipmentData.modelIconUrl}
            type={equipmentData.type}
            subcategory={equipmentData.subcategory}
            nickName={equipmentData.nickName}
            model={equipmentData.model}
            close={() => setShowDeleteEquipmentDialog(false)}
            deleteEquipment={onDeleteEquipment}
          />
        </Dialog>
      )}
      {showLocationUnavailableDialog && (
        <LocationUnavailableGeofenceDialog
          close={() => {
            setShowLocationUnavailableDialog(false);
          }}
        />
      )}
      <div>
        {nicknameIsExpanded && (
          <EditNickname
            closeDialog={() => {
              setNicknameIsExpanded(false);
            }}
          />
        )}

        {engineHoursIsExpanded && (
          <EditEngineHours
            closeDialog={() => {
              setEngineHoursIsExpanded(false);
            }}
          />
        )}
        <div className="equipment-content-information section section-grey">
          <div className="container">
            <div className="relative">
              <span
                className="back-button back-button-on-layout absolute back-button-equipment-mobileonly"
                onClick={() => handleClick()}
              >
                <IconBack />
              </span>
              <GridLayout
                className="equipment-header-section"
                cols={[{ width: '60%' }, { width: '40%' }]}
              >
                <GridLayoutItem>
                  <div className="mobile-row">
                    <div className="title-wrapper">
                      <div className="equipment-content__title-block">
                        <h1
                          className="equipment-content__title"
                          title={equipmentData.nickName || equipmentData.model}
                        >
                          {!!equipmentData.smartAttachment && (
                            <img
                              className="equipment-content__intel-icon"
                              src={IntelIcon}
                              alt="Intel"
                            />
                          )}

                          {isEV && (
                            <img
                              className="equipment-content__intel-icon"
                              src={EvEquipmentIcon}
                              alt="Ev Equipment"
                            />
                          )}
                          <span className="equipment-content__text">
                            {equipmentData.nickName || equipmentData.model}
                          </span>
                        </h1>

                        {isAccessSharingFeatureEnabled && (
                          <EquipmentAccessSharingButton
                            className="equipment-content__access-sharing-button"
                            equipment={equipmentData}
                          />
                        )}

                        {ellipsisMenuVisibility && (
                          <EllipsisMenu
                            actions={actions}
                            menuClassName="equipment-content-dropdown"
                          />
                        )}
                      </div>

                      <div className="inline-label-text">
                        <span className="label">{t('equipment_model')}</span>
                        <span className="text">{equipmentData.model}</span>
                      </div>
                      <div className="inline-label-text">
                        <span className="label">
                          {equipmentData.identifierType === 'Pin'
                            ? t('equipment_pin')
                            : t('equipment_serial')}{' '}
                        </span>
                        <span className="text">
                          {equipmentData.pinOrSerial}
                        </span>
                      </div>
                      <div className="inline-label-text">
                        <span className="label">{t('equipment_hours')}</span>
                        <span className="text">{getHours(data)}</span>
                      </div>
                    </div>
                    <div className="equipment-content-image-container mobile-only">
                      <img
                        src={
                          equipmentData.modelFullUrl ||
                          equipmentData.modelIconUrl ||
                          getDefaultThumbnail(
                            equipmentData.type,
                            equipmentData.subcategory,
                          )
                        }
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null;
                          currentTarget.src = getDefaultThumbnail(
                            equipmentData.type,
                            equipmentData.subcategory,
                          );
                        }}
                        alt=""
                        className="equipment-content-image"
                      />
                    </div>
                  </div>
                  {(equipmentData.telematics?.faultCodes?.length || 0) > 0 && (
                    <div
                      className="errors-box errors-box-with-warning-text"
                      onClick={() => {
                        navigate(location.pathname + '/faultCodes');
                      }}
                    >
                      <div>
                        <b>{t('warnings')}</b>
                      </div>
                      <div className="equipment-list-fault-codes-box">
                        <div>
                          <span className="equipment-list-fault-codes-icon">
                            <img src={AssetTriangleIcon} />
                            <span className="equipment-list-fault-codes-text">
                              {equipmentData.telematics!.faultCodes?.length ||
                                0}
                            </span>
                          </span>
                        </div>
                        <div className="warning-texts-wrapper">
                          <span className="alert">
                            {[...(equipmentData.telematics!.faultCodes || [])]
                              .sort((a, b) =>
                                Date.parse(a.timeReported) <
                                Date.parse(b.timeReported)
                                  ? -1
                                  : Date.parse(a.timeReported) >
                                    Date.parse(b.timeReported)
                                  ? 1
                                  : 0,
                              )
                              ?.map((faultCode, index) => {
                                if (faultCode.j1939Fmi || faultCode.j1939Spn) {
                                  return (
                                    <div key={index} className="ellipsis">
                                      <b>
                                        J1939: {faultCode.j1939Spn}/
                                        {faultCode.j1939Fmi}
                                      </b>{' '}
                                      -{' '}
                                      {faultCode.description ||
                                        t('no_description_available')}
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div key={index} className="ellipsis">
                                      <b>E:{faultCode.code} - </b>
                                      {faultCode.description ||
                                        t('no_description_available')}
                                    </div>
                                  );
                                }
                              })}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </GridLayoutItem>
                <GridLayoutItem>
                  <div className="equipment-content-image-container">
                    <img
                      src={
                        equipmentData.modelFullUrl ||
                        equipmentData.modelIconUrl ||
                        getDefaultThumbnail(
                          equipmentData.type,
                          equipmentData.subcategory,
                        )
                      }
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = getDefaultThumbnail(
                          equipmentData.type,
                          equipmentData.subcategory,
                        );
                      }}
                      alt=""
                      className="equipment-content-image"
                    />
                  </div>
                </GridLayoutItem>
              </GridLayout>

              {isSubscriptionsFeatureEnabled &&
                isTelematics &&
                !subscriptionInfo.hasPremium && (
                  <SubscriptionBlock
                    className="equipment-content__subscription-block_mobile"
                    buttonLabel={t('upgrade')}
                    onClick={() =>
                      openManageYourPlanDialog({ equipment: equipmentData })
                    }
                  />
                )}

              {equipmentData.telematics && (
                <div className="equipment-content-buttons-container">
                  <Button>
                    <RestartInhibitStatusIcon
                      telematics={equipmentData.telematics}
                    />
                  </Button>
                  <Button>
                    <InMotionStatusIcon
                      telematics={equipmentData.telematics!}
                    />
                  </Button>
                </div>
              )}
              <div>
                {equipmentData.telematics && (
                  <GridLayout
                    className="equipment-info-container"
                    cols={
                      hasFuelOrDef || isEV
                        ? [{ width: '50%' }, { width: '50%' }]
                        : [{ width: '25%' }, { width: '75%' }]
                    }
                  >
                    <GridLayoutItem>
                      {isEV ? (
                        <Card className={'ev-battery-card'}>
                          <div className="row">
                            <span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="36"
                                height="76"
                                viewBox="0 0 36 76"
                              >
                                <g id="bg_battery_small" fill="none">
                                  <path
                                    d="M2,36a2,2,0,0,1-2-2V3.687a2,2,0,0,1,2-2H6.111V1a1,1,0,0,1,1-1H12.89a1,1,0,0,1,1,1v.688H18a2,2,0,0,1,2,2V34a2,2,0,0,1-2,2Z"
                                    stroke="none"
                                  />
                                  <rect
                                    x={2.5}
                                    y={76 - filledHeight - marginBottom}
                                    width={31}
                                    height={filledHeight}
                                    stroke="none"
                                    fill={fillColor}
                                  />
                                  {batteryStatusSvg}
                                  <path
                                    d="M 32.9994010925293 74.99960327148438 C 34.10253143310547 74.99960327148438 35 74.10253143310547 35 72.99990081787109 L 35 6.562801837921143 C 35 5.459668159484863 34.10253143310547 4.562201499938965 32.9994010925293 4.562201499938965 L 24.00020027160645 4.562201499938965 L 24.00029945373535 1.000001668930054 L 11.99979972839355 0.9999016523361206 L 11.99979972839355 4.562201499938965 L 2.99970006942749 4.562201499938965 C 1.897066712379456 4.562201499938965 1 5.459668159484863 1 6.562801837921143 L 1 72.99990081787109 C 1 74.10253143310547 1.897066712379456 74.99960327148438 2.99970006942749 74.99960327148438 L 32.9994010925293 74.99960327148438 M 32.9994010925293 75.99960327148438 L 2.99970006942749 75.99960327148438 C 1.34280002117157 75.99960327148438 0 74.65679931640625 0 72.99990081787109 L 0 6.562801837921143 C 0 4.905901432037354 1.34280002117157 3.562201738357544 2.99970006942749 3.562201738357544 L 10.99979972839355 3.562201738357544 L 10.99979972839355 0.9999016523361206 C 10.99979972839355 0.4473016262054443 11.44799995422363 1.635742137295892e-06 11.99969959259033 1.635742137295892e-06 L 24.00029945373535 1.635742137295892e-06 C 24.55200004577637 1.635742137295892e-06 25.00020027160645 0.4473016262054443 25.00020027160645 0.9999016523361206 L 25.00020027160645 3.562201738357544 L 32.9994010925293 3.562201738357544 C 34.65629959106445 3.562201738357544 36 4.905901432037354 36 6.562801837921143 L 36 72.99990081787109 C 36 74.65679931640625 34.65629959106445 75.99960327148438 32.9994010925293 75.99960327148438 Z"
                                    stroke="none"
                                    fill="#1a1a1a"
                                  />
                                </g>
                              </svg>
                            </span>
                            {!batteryHasState ? (
                              <div className="ev-battery-state-info">
                                <span className="equipment-list-telematic-value">
                                  {Math.round(
                                    equipmentData.telematics?.electric?.battery
                                      .remainingCharge || 0,
                                  )}
                                  %
                                </span>
                              </div>
                            ) : (
                              <div className="ev-battery-state-info">
                                <span className="equipment-list-telematic-value">
                                  {Math.round(
                                    equipmentData.telematics?.electric?.battery
                                      .remainingCharge || 0,
                                  )}
                                  %
                                </span>
                                <span className="battery-status">
                                  {batteryStatusState}
                                </span>
                              </div>
                            )}
                          </div>
                          {equipmentData.telematics?.electric
                            ?.remainingRange && (
                            <div className="ev-battery-duration-info">
                              <span>{t('range_remaining')}</span>
                              <span className="equipment-list-telematic-value">
                                {convertNumberToDistance(
                                  equipmentData.telematics?.electric
                                    ?.remainingRange?.distance ?? 0,
                                  measurementUnit,
                                )}
                                &nbsp;&nbsp;&nbsp;&nbsp;
                                {formatSeconds(
                                  equipmentData.telematics?.electric
                                    ?.remainingRange?.time ?? 0,
                                )}
                              </span>
                            </div>
                          )}
                        </Card>
                      ) : (
                        <GridLayout
                          className={`equipment-content-values-grid ${
                            !hasFuelOrDef ? 'no-fuel-or-def' : ''
                          }`}
                          cols={
                            hasFuelOrDef
                              ? [
                                  { width: 'auto' },
                                  { width: 'auto' },
                                  { width: 'auto' },
                                ]
                              : [{ width: '100%' }]
                          }
                        >
                          <GridLayoutItem>
                            <Card className={'equipment-content-value_card'}>
                              <label>{t('hours')}</label>
                              <span className="equipment-content-telematic-value">
                                <ClockIcon width={22} height={22} />
                                {getHours(data)}
                              </span>
                            </Card>
                          </GridLayoutItem>
                          <GridLayoutItem>
                            <Card className={'equipment-content-value_card'}>
                              <label>{t('engine_battery')}</label>
                              <span className="equipment-list-telematic-value">
                                {(equipmentData.telematics?.extPowerVolts ||
                                  0) >= 12.5 && (
                                  <img
                                    src={NormalBateryIcon}
                                    alt="React Logo"
                                  />
                                )}
                                {(equipmentData.telematics?.extPowerVolts ||
                                  0) >= 12.3 &&
                                  (equipmentData.telematics?.extPowerVolts ||
                                    0) < 12.5 && (
                                    <img
                                      src={WarningBateryIcon}
                                      alt="React Logo"
                                    />
                                  )}
                                {(equipmentData.telematics?.extPowerVolts ||
                                  0) < 12.3 && (
                                  <img
                                    src={CriticalBateryIcon}
                                    alt="React Logo"
                                  />
                                )}{' '}
                                {aproximateToTwo(
                                  equipmentData.telematics?.extPowerVolts,
                                )}
                                V
                              </span>
                            </Card>
                          </GridLayoutItem>
                          {hasFuel && (
                            <GridLayoutItem>
                              <Card className={'equipment-content-value_card'}>
                                <label>{t('fuel')}</label>
                                <span className="equipment-content-telematic-value">
                                  <span
                                    className={`equipment-content-semi-donut equipment-content-semi-donut-${Math.round(
                                      equipmentData.telematics
                                        .fuelRemainingPercent,
                                    )} equipment-content-semi-donut-${
                                      equipmentData.telematics
                                        .fuelRemainingPercent >= 26
                                        ? 'normal'
                                        : equipmentData.telematics
                                            .fuelRemainingPercent >= 11
                                        ? 'warning'
                                        : 'critical'
                                    }`}
                                  />{' '}
                                  {aproximateToTwo(
                                    equipmentData.telematics
                                      .fuelRemainingPercent,
                                  )}
                                  %
                                </span>
                              </Card>
                            </GridLayoutItem>
                          )}
                          {hasDef && (
                            <GridLayoutItem>
                              <Card className={'equipment-content-value_card'}>
                                <label>{t('def')}</label>
                                <span className="equipment-content-telematic-value">
                                  <span
                                    className={`equipment-content-semi-donut equipment-content-semi-donut-${Math.round(
                                      equipmentData.telematics
                                        .defRemainingPercent,
                                    )} equipment-content-semi-donut-${
                                      equipmentData.telematics
                                        .defRemainingPercent >= 36
                                        ? 'normal'
                                        : equipmentData.telematics
                                            .defRemainingPercent >= 16
                                        ? 'warning'
                                        : 'critical'
                                    }`}
                                  />{' '}
                                  {aproximateToTwo(
                                    equipmentData.telematics
                                      .defRemainingPercent,
                                  )}
                                  %
                                </span>
                              </Card>
                            </GridLayoutItem>
                          )}
                          {hasFuelOrDef && (
                            <>
                              <GridLayoutItem>
                                <Card
                                  className={'equipment-content-value_card'}
                                >
                                  <label>{t('hydraulic_temp')}</label>
                                  <span className="equipment-list-telematic-value">
                                    {(equipmentData.telematics
                                      ?.hydraulicTempCelsius || 0) <= 100 && (
                                      <img
                                        src={NormalTemperatureIcon}
                                        alt="React Logo"
                                      />
                                    )}
                                    {(equipmentData.telematics
                                      ?.hydraulicTempCelsius || 0) > 100 &&
                                      (equipmentData.telematics
                                        ?.hydraulicTempCelsius || 0) <= 110 && (
                                        <img
                                          src={WarningTemperatureIcon}
                                          alt="React Logo"
                                        />
                                      )}
                                    {(equipmentData.telematics
                                      ?.hydraulicTempCelsius || 0) > 110 && (
                                      <img
                                        src={CriticalTemperatureIcon}
                                        alt="React Logo"
                                      />
                                    )}{' '}
                                    {aproximateToTwo(
                                      temperatureUnitConverted(
                                        equipmentData.telematics
                                          ?.hydraulicTempCelsius,
                                      ),
                                    )}
                                    {measurementUnit === 'METRIC' ? 'C' : 'F'}
                                  </span>
                                </Card>
                              </GridLayoutItem>
                              <GridLayoutItem>
                                <Card
                                  className={'equipment-content-value_card'}
                                >
                                  <label>{t('coolant_temp')}</label>
                                  <span className="equipment-list-telematic-value">
                                    {(equipmentData.telematics
                                      ?.coolantTempCelsius || 0) <= 100 && (
                                      <img
                                        src={NormalTemperatureIcon}
                                        alt="React Logo"
                                      />
                                    )}
                                    {(equipmentData.telematics
                                      ?.coolantTempCelsius || 0) > 100 &&
                                      (equipmentData.telematics
                                        ?.coolantTempCelsius || 0) <= 110 && (
                                        <img
                                          src={WarningTemperatureIcon}
                                          alt="React Logo"
                                        />
                                      )}
                                    {(equipmentData.telematics
                                      ?.coolantTempCelsius || 0) > 110 && (
                                      <img
                                        src={CriticalTemperatureIcon}
                                        alt="React Logo"
                                      />
                                    )}{' '}
                                    {aproximateToTwo(
                                      temperatureUnitConverted(
                                        equipmentData.telematics
                                          ?.coolantTempCelsius,
                                      ),
                                    )}
                                    {measurementUnit === 'METRIC' ? 'C' : 'F'}
                                  </span>
                                </Card>
                              </GridLayoutItem>
                            </>
                          )}
                        </GridLayout>
                      )}
                    </GridLayoutItem>
                    <GridLayoutItem>
                      {!isLoaded ? (
                        <WordmarkLoader title={t('loading_map')} />
                      ) : (
                        <Card
                          onClick={() => {
                            if (
                              equipmentData.telematics?.location?.latitude &&
                              equipmentData.telematics?.location?.longitude
                            ) {
                              setData(oldData => ({
                                ...oldData,
                                panTo: {
                                  position: {
                                    lat: equipmentData.telematics?.location
                                      ?.latitude as any,
                                    lng: equipmentData.telematics?.location
                                      ?.longitude as any,
                                  },
                                  zoom: 14,
                                },
                              }));
                              navigate('/equipment/geofences');
                            } else {
                              setShowLocationUnavailableDialog(true);
                            }
                          }}
                          className={`eq-details-map`}
                        >
                          <div className="unavailable-map-wrapper">
                            <GoogleMap
                              mapContainerStyle={mapContainerStyle}
                              center={{
                                lat:
                                  (equipmentData.telematics?.location
                                    ?.latitude as any) || 32.9792895,
                                lng:
                                  (equipmentData.telematics?.location
                                    ?.longitude as any) || -97.0315917,
                              }}
                              zoom={10}
                              options={{
                                keyboardShortcuts: false,
                                disableDefaultUI: true,
                                disableDoubleClickZoom: true,
                                scrollwheel: false,
                                draggable: false,
                              }}
                            >
                              {data.geoAddress && (
                                <MarkerF
                                  icon={{
                                    url: DealerIcon,
                                  }}
                                  key={
                                    'mapContentMarkerFor' +
                                    equipmentData.id +
                                    data.geoAddress?.toString()
                                  }
                                  position={{
                                    lat: equipmentData.telematics?.location
                                      ? equipmentData.telematics?.location
                                          .latitude
                                      : 32.97932,
                                    lng: equipmentData.telematics?.location
                                      ? equipmentData.telematics?.location
                                          .longitude
                                      : -97.029314,
                                  }}
                                ></MarkerF>
                              )}
                            </GoogleMap>
                            {!data.geoAddress && (
                              <span className="unavailable-pin">
                                <img
                                  src={MapUnavailablePinIcon}
                                  alt="React Logo"
                                />
                              </span>
                            )}
                          </div>
                          <div className="relative">
                            <div className="r-geo-location-box eq-location-box">
                              <div
                                className="geo-address"
                                title={
                                  data.geoAddress || t('location_unavailable1')
                                }
                              >
                                {data.geoAddress || t('location_unavailable1')}
                              </div>
                              {data.geoAddress && (
                                <div className="geo-time">{getDate()}</div>
                              )}
                            </div>
                          </div>
                        </Card>
                      )}
                    </GridLayoutItem>
                  </GridLayout>
                )}
              </div>
            </div>
          </div>
        </div>
        {equipmentData.telematics?.smartAttachment && (
          <div className="equipment-intel-info section">
            <div className="container">
              <div className="intel-info-link-icon">
                <img src={EquipmentLinkIcon} alt="Equipment Link Icon"></img>
              </div>
              <div className="intel-box">
                <div className="intel-box_image">
                  <img
                    src={smartAttachmentImageUrl}
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = getDefaultThumbnail(
                        smartAttachmentInfo?.type,
                        smartAttachmentInfo?.subcategory,
                      );
                    }}
                    alt="Smart Attachment"
                  />
                </div>
                <div className="intel-box_text">
                  <div>{t('equipment_intell_attach')}</div>
                  <span
                    className="equipment-list-nickname"
                    title={equipmentData.telematics?.smartAttachment.model}
                  >
                    <img src={IntelIcon} alt="Intel" />
                    <span>
                      {equipmentData.telematics?.smartAttachment.model}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="equipment-content-actions section">
          <div className="container">
            {equipmentData.smartAttachment && (
              <>
                <h4>{t('equipment_intell_attach')}</h4>
                <div className="intel-attach-tag">
                  <div className="intel-box">
                    <div className="intel-box_image">
                      <img src={RessIntelAttachIcon} alt="Ress Intel Attach" />
                    </div>
                    <div className="intel-box_text">
                      <div>{t('equipment_tag')}</div>
                      <span>
                        {t('equipment_serial')}
                        <span> {equipmentData.smartAttachment?.tagSerial}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}

            <>
              {equipmentData.type == 'attachment' ? (
                <CompatibleMachines
                  key={equipmentData.id}
                  currentEquipment={equipmentData}
                />
              ) : (
                <CompatibleAttachments
                  key={equipmentData.id}
                  currentEquipment={equipmentData}
                />
              )}
            </>
            <div className="row equipment-content-links">
              {equipmentData.telematics && (
                <div className="col-sm-6">
                  {!isRestartInhibitUnavailable && (
                    <>
                      <h4 className="equipment-content__section-title">
                        {t('title_machine_controls')}
                      </h4>

                      <InhibitRestartButton
                        key={equipmentData.id}
                        equipmentId={equipmentData.id}
                        telematics={equipmentData.telematics}
                        hasAccess={hasAccess('editInhibitRestart')}
                        subscriptionInfo={subscriptionInfo}
                      />
                    </>
                  )}

                  <div className="separator-xl"></div>

                  <div>
                    <h4 className="equipment-content__section-title">
                      <span>{t('kubota')}</span>
                      <span className="equipment-content-actions-italic">
                        {t('now')}
                      </span>
                    </h4>
                    {isSubscriptionsFeatureEnabled &&
                      !subscriptionInfo.hasPremium && (
                        <SubscriptionBlock
                          className="equipment-content__subscription-block_desktop"
                          onClick={() =>
                            openManageYourPlanDialog({
                              equipment: equipmentData,
                            })
                          }
                        />
                      )}
                    {!isSubscriptionsFeatureEnabled ? (
                      <div className="arrow-links arrow-links-no-icon-border">
                        <div
                          className="arrow-links_item"
                          onClick={() => {
                            if (
                              equipmentData.telematics?.location?.latitude &&
                              equipmentData.telematics?.location?.longitude
                            ) {
                              setData(oldData => ({
                                ...oldData,
                                panTo: {
                                  position: {
                                    lat: equipmentData.telematics?.location
                                      ?.latitude as any,
                                    lng: equipmentData.telematics?.location
                                      ?.longitude as any,
                                  },
                                  zoom: 14,
                                },
                              }));
                              navigate('/equipment/geofences');
                            } else {
                              setShowLocationUnavailableDialog(true);
                            }
                          }}
                        >
                          <div className="image-with-dot">
                            <img
                              className="arrow-links_icon"
                              src={GeofenceIcon}
                            ></img>
                            {_.isEmpty(
                              equipmentData.telematics.insideGeofences,
                            ) && <span className="red-dot" />}
                          </div>
                          <span>{t('geofence')}</span>
                          <img
                            className="arrow-links_icon arrow-links_icon-right"
                            src={ChevronRightIcon}
                          ></img>
                        </div>
                      </div>
                    ) : (
                      <>
                        <List>
                          {subscriptionInfo.hasPremium && (
                            <ListItemButton
                              className="equipment-content__premium-plan"
                              onClick={() => {
                                openManageYourPlanDialog({
                                  equipment: equipmentData,
                                });
                              }}
                            >
                              <ListItemIcon>
                                <DiamondIcon className="equipment-content__premium-plan-icon" />
                              </ListItemIcon>
                              <ListItemText
                                title={t(
                                  `subscriptions.equipment_details.trigger_button.${
                                    subscriptionInfo.isInitial
                                      ? 'initial_title'
                                      : 'premium_title'
                                  }`,
                                )}
                                subtitle={
                                  subscriptionInfo.endsWithinDays ||
                                  subscriptionInfo.nextPaymentDate ? (
                                    <span className="equipment-content__next-payment">
                                      {t(
                                        `subscriptions.equipment_details.trigger_button.${
                                          subscriptionInfo.endsWithinDays
                                            ? 'expires_on'
                                            : 'next_payment'
                                        }`,
                                      )}{' '}
                                      <span className="equipment-content__due-to">
                                        {subscriptionInfo.endsWithinDays
                                          ? subscriptionInfo.endsOn
                                          : subscriptionInfo.nextPaymentDate}
                                      </span>
                                    </span>
                                  ) : undefined
                                }
                              />
                            </ListItemButton>
                          )}

                          <ListItemButton
                            onClick={() => {
                              if (
                                equipmentData.telematics?.location?.latitude &&
                                equipmentData.telematics?.location?.longitude
                              ) {
                                setData(oldData => ({
                                  ...oldData,
                                  panTo: {
                                    position: {
                                      lat: equipmentData.telematics?.location
                                        ?.latitude as any,
                                      lng: equipmentData.telematics?.location
                                        ?.longitude as any,
                                    },
                                    zoom: 14,
                                  },
                                }));
                                navigate('/equipment/geofences');
                              } else {
                                setShowLocationUnavailableDialog(true);
                              }
                            }}
                          >
                            <ListItemIcon>
                              {subscriptionInfo.hasPremium ? (
                                <GeofenceIconNew />
                              ) : (
                                <NoNotificationIcon />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              title={t('geofence')}
                              subtitle={
                                !subscriptionInfo.hasPremium
                                  ? {
                                      i18nKey: 'geofence_unlock_notifications',
                                    }
                                  : undefined
                              }
                            />
                          </ListItemButton>
                        </List>
                      </>
                    )}
                  </div>
                </div>
              )}
              <div
                className={equipmentData.telematics ? 'col-sm-6' : 'col-sm-12'}
              >
                <div className="equipment-content-actions-title">
                  <h4 className="equipment-content__section-title">
                    {t('title_resources')}
                  </h4>
                </div>
                <div className="arrow-links arrow-links-no-icon-border">
                  {equipmentData.type === 'machine' && maintenanceLoading && (
                    <div
                      className="arrow-links_item arrow-links_item-loading"
                      onClick={() =>
                        navigate(location.pathname + '/trackMaintenance')
                      }
                    >
                      <img
                        className="arrow-links_icon"
                        src={TrackMaintenanceIcon}
                      ></img>
                      <span className="content-container">
                        <span>{t('track_maintenance')}</span>
                      </span>
                      <div className="loading_icon">
                        <div className="css-icon"></div>
                      </div>
                      <img
                        className="arrow-links_icon arrow-links_icon-right"
                        src={ChevronRightIcon}
                      ></img>
                    </div>
                  )}
                  {equipmentData.type === 'machine' &&
                    !maintenanceLoading &&
                    (maintenanceHistoryData.maintenanceIntervals || [])
                      ?.length > 0 && (
                      <div
                        className="arrow-links_item"
                        onClick={() =>
                          navigate(location.pathname + '/trackMaintenance')
                        }
                      >
                        <div className="image-with-dot">
                          <img
                            className="arrow-links_icon"
                            src={TrackMaintenanceIcon}
                          ></img>
                          {!lastHistoryInterval && <span className="red-dot" />}
                        </div>

                        <span className="content-container">
                          <span>{t('track_maintenance')}</span>
                          <span className="arrow-links_subtitle">
                            {lastHistoryInterval &&
                              t('track_maintenance_status', {
                                date: lastUpdate,
                                lastHours: getMaintenanceHours(
                                  lastHistoryInterval.completedEngineHours,
                                ),
                                nextHours: nextHours,
                              })}
                          </span>
                        </span>
                        <img
                          className="arrow-links_icon arrow-links_icon-right"
                          src={ChevronRightIcon}
                        ></img>
                      </div>
                    )}

                  {equipmentData.hasFaultCodes && (
                    <div
                      className="arrow-links_item"
                      onClick={() =>
                        navigate(location.pathname + '/faultCodes')
                      }
                    >
                      <div className="image-with-dot">
                        <img
                          className="arrow-links_icon"
                          src={FaultCodeIcon}
                        ></img>
                        {(equipmentData.telematics?.faultCodes?.length || 0) >
                          0 && <span className="red-dot" />}
                      </div>

                      <span>{t('fault_code_lookup')}</span>
                      <img
                        className="arrow-links_icon arrow-links_icon-right"
                        src={ChevronRightIcon}
                      ></img>
                    </div>
                  )}
                  {equipmentData.manualEntries &&
                    equipmentData.manualEntries.length > 0 && (
                      <div
                        className="arrow-links_item"
                        onClick={() => navigate(location.pathname + '/manual')}
                      >
                        <img
                          className="arrow-links_icon"
                          src={ManualsIcon}
                        ></img>
                        <span>{t('manuals')}</span>
                        <img
                          className="arrow-links_icon arrow-links_icon-right"
                          src={ChevronRightIcon}
                        ></img>
                      </div>
                    )}
                  {equipmentData.guideUrl && (
                    <div
                      className="arrow-links_item"
                      onClick={() => navigate(location.pathname + '/guides')}
                    >
                      <img
                        className="arrow-links_icon"
                        src={MaintenanceGuides}
                      ></img>
                      <span>{t('maintenance_guides')}</span>
                      <img
                        className="arrow-links_icon arrow-links_icon-right"
                        src={ChevronRightIcon}
                      ></img>
                    </div>
                  )}
                  {equipmentData.hasMaintenanceSchedules && (
                    <div
                      className="arrow-links_item"
                      onClick={() =>
                        navigate(location.pathname + '/maintenanceSchedule')
                      }
                    >
                      <img
                        className="arrow-links_icon"
                        src={MaintenanceScheduleIcon}
                      ></img>
                      <span>{t('maintenance_schedules')}</span>
                      <img
                        className="arrow-links_icon arrow-links_icon-right"
                        src={ChevronRightIcon}
                      ></img>
                    </div>
                  )}
                  {equipmentData.warrantyUrl &&
                    equipmentData.type === 'machine' && (
                      <div
                        className="arrow-links_item"
                        onClick={() => {
                          window.open(equipmentData.warrantyUrl, '_blank');
                        }}
                      >
                        <img
                          className="arrow-links_icon"
                          src={WarrantyInformationIcon}
                        ></img>
                        <span>{t('warranty_info')}</span>
                        <img
                          className="arrow-links_icon arrow-links_icon-right"
                          src={ChevronRightIcon}
                        ></img>
                      </div>
                    )}
                  {equipmentData.videoEntries &&
                    equipmentData.videoEntries.length > 0 && (
                      <div
                        className="arrow-links_item"
                        onClick={() =>
                          navigate(location.pathname + '/videoResources')
                        }
                      >
                        <img
                          className="arrow-links_icon"
                          src={VideoResourcesIcon}
                        ></img>
                        <span>{t('instructional_videos_title')}</span>
                        <img
                          className="arrow-links_icon arrow-links_icon-right"
                          src={ChevronRightIcon}
                        ></img>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
